import { useState, useEffect, useCallback } from 'react'
import { PageSkeleton } from '../components/PageSkeletons'
import { createPortal } from 'react-dom'
import { groupsApi, adminApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import UserAvatar from '../components/UserAvatar'
import {
  Users as UsersIcon, Plus, Search, MoreVertical, Trash2, Edit3,
  Shield, Globe, Loader2, X, AlertTriangle,
  DownloadCloud, RefreshCw, Clock, Info,
  List, LayoutGrid,
} from 'lucide-react'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import { useAuth } from '../context/AuthContext'

// ─── Confirm Modal ───
function ConfirmModal({ open, title, message, confirmLabel, onConfirm, onCancel, loading }) {
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2.5 mt-6">
          <button onClick={onCancel} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Create/Edit Group Modal ───
function GroupModal({ open, group, onClose, onSave, loading }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (open) {
      setName(group?.name || '')
      setDescription(group?.description || '')
    }
  }, [open, group])

  if (!open) return null

  const isEdit = !!group
  const canSave = name.trim().length > 0

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {isEdit ? 'Edit Group' : 'Create Manual Group'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isEdit ? 'Update the group name or description.' : 'Create a custom group to organize users manually.'}
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Group Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Finance Team"
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this group..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2.5 mt-6">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => onSave({ name: name.trim(), description: description.trim() })} disabled={!canSave || loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-navy-800 hover:bg-navy-900 text-white disabled:opacity-50">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Import Entra Group Modal ───
function ImportEntraModal({ open, onClose, onImport, loading }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [message, setMessage] = useState('')
  const [importingId, setImportingId] = useState(null)

  const searchGroups = useCallback(async () => {
    if (!query.trim()) return
    setSearching(true)
    setMessage('')
    try {
      const res = await groupsApi.searchEntra(query)
      setResults(res.data?.groups || [])
      if (res.data?.graph_configured === false) setMessage(res.data.message)
      else if ((res.data?.groups || []).length === 0) setMessage('No groups found matching your search.')
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to search Entra groups')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [query])

  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setMessage('')
      setImportingId(null)
    }
  }, [open])

  if (!open) return null

  const handleImport = async (groupId) => {
    setImportingId(groupId)
    await onImport(groupId)
    setImportingId(null)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Import from Microsoft Entra ID</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Search and import Entra groups. Members that match existing users in your organisation will be automatically linked.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-6 pt-5 pb-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchGroups() }}
                placeholder="Type a group name and press Enter..."
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button onClick={searchGroups} disabled={searching || loading || !query.trim()}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-navy-800 hover:bg-navy-900 text-white text-sm font-medium disabled:opacity-50 transition-colors">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {message && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 mb-3">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">{message}</p>
            </div>
          )}

          {searching && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Searching Entra groups...</span>
            </div>
          )}

          {!searching && results.length > 0 && (
            <div className="space-y-2">
              {results.map(group => (
                <div key={group.id} className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{group.displayName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {group.description || group.mail || group.id}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleImport(group.id)}
                    disabled={loading || importingId === group.id}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    {importingId === group.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5" />}
                    Import
                  </button>
                </div>
              ))}
            </div>
          )}

          {!searching && results.length === 0 && !message && (
            <div className="text-center py-12">
              <Globe className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Search for an Entra group by name to get started.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Members Modal (Entra: view-only list / Manual: editable picker) ───
function MembersModal({ open, group, orgUsers, onClose, onSave, onSync, loading, readOnly }) {
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (open && group) {
      setSelectedIds(new Set((group.members || []).map(m => m.user_id)))
      setSearch('')
    }
  }, [open, group])

  if (!open || !group) return null

  const isEntra = group.type === 'entra'
  const members = group.members || []

  const getFilteredList = () => {
    const q = search.toLowerCase()
    if (isEntra) {
      return members.filter(m =>
        (m.username || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      )
    }
    // Manual: show selected first, then unselected
    const filtered = orgUsers.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    )
    return filtered.sort((a, b) => {
      const aSelected = selectedIds.has(a.id) ? 0 : 1
      const bSelected = selectedIds.has(b.id) ? 0 : 1
      return aSelected - bSelected
    })
  }

  const filteredList = getFilteredList()

  const toggle = (userId) => {
    if (isEntra || readOnly) return
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl max-h-[75vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isEntra ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
              }`}>
                {isEntra
                  ? <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  : <UsersIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                }
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{group.name}</h3>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  isEntra
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                }`}>
                  {isEntra ? 'Entra ID Group' : 'Manual Group'}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Context info */}
          <div className={`mt-4 flex items-start gap-2.5 p-3 rounded-lg ${
            isEntra
              ? 'bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30'
              : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800'
          }`}>
            <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isEntra ? 'text-blue-500' : 'text-gray-400'}`} />
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {isEntra ? (
                <>
                  <p>This group is synced from Microsoft Entra ID. Membership cannot be edited manually.</p>
                  {group.last_synced_at && (
                    <p className="mt-1 text-gray-400 dark:text-gray-500">
                      Last synced: {new Date(group.last_synced_at).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                <p>Select users below to add or remove them from this group. Changes apply when saved.</p>
              )}
            </div>
          </div>

          {/* Search + count row */}
          <div className="mt-4 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isEntra ? 'Search synced members...' : 'Search users to add...'}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {isEntra
                ? `${members.length} member${members.length !== 1 ? 's' : ''}`
                : `${selectedIds.size} / ${orgUsers.length} selected`
              }
            </p>
          </div>
          {!isEntra && selectedIds.size > 0 && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* User list - capped height, two-column for wider modal */}
        <div className="flex-1 overflow-y-auto px-6 pt-3 pb-2 min-h-0" style={{ maxHeight: '45vh' }}>
          {isEntra ? (
            filteredList.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {filteredList.map(m => (
                  <div key={m.user_id || m.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
                    <UserAvatar user={{ id: m.user_id || m.id, username: m.username, email: m.email, role: m.role }} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{m.username || m.email}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{m.email}</p>
                    </div>
                    {m.role && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                        m.role === 'admin' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {m.role}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UsersIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {search ? 'No members match your search.' : 'No matching users found. Try syncing from Entra.'}
                </p>
              </div>
            )
          ) : (
            filteredList.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {filteredList.map(u => {
                  const isSelected = selectedIds.has(u.id)
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggle(u.id)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all ${
                        isSelected
                          ? 'bg-purple-50 dark:bg-purple-900/15 border border-purple-200 dark:border-purple-800'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/60 border border-transparent'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <UserAvatar user={u} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{u.username || u.email}</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                        u.role === 'admin' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {u.role}
                      </span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">No users match your search.</p>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            {isEntra ? 'Close' : 'Cancel'}
          </button>
          {isEntra ? (
            <button onClick={onSync} disabled={loading || readOnly}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync from Entra
            </button>
          ) : (
            <button onClick={() => !readOnly && onSave([...selectedIds])} disabled={loading || readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-navy-800 hover:bg-navy-900 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Members
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Main Groups Page ───
export default function Groups({ activeTab = 'groups', onTabChange, tabs = [] }) {
  const toast = useToast()
  const { user: authUser } = useAuth()
  const [groups, setGroups] = useState([])
  const [orgUsers, setOrgUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [groupTypeFilter, setGroupTypeFilter] = useState('all') // all | entra | manual
  const [viewMode, setViewMode] = useState('list') // list | grid
  const [actionLoading, setActionLoading] = useState(false)
  const [importModal, setImportModal] = useState(false)

  // Modals
  const [groupModal, setGroupModal] = useState({ open: false, group: null })
  const [membersModal, setMembersModal] = useState({ open: false, group: null })
  const [deleteModal, setDeleteModal] = useState({ open: false, group: null })

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, usersRes] = await Promise.all([
        groupsApi.list(),
        adminApi.listUsers(),
      ])
      setGroups(groupsRes.data)
      setOrgUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.users || [])
    } catch (err) {
      toast.error('Error', 'Failed to load groups')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { fetchData() }, [fetchData])

  const filtered = groups.filter(g => {
    const q = search.toLowerCase()
    const matchesSearch = g.name.toLowerCase().includes(q) || (g.description || '').toLowerCase().includes(q)
    const matchesType = groupTypeFilter === 'all' || g.type === groupTypeFilter
    return matchesSearch && matchesType
  })

  // Stats
  const entraCount = groups.filter(g => g.type === 'entra').length
  const manualCount = groups.filter(g => g.type === 'manual').length
  const statCards = [
    {
      key: 'all',
      count: groups.length,
      label: 'Total Groups',
      icon: UsersIcon,
      iconWrap: 'bg-gray-100 dark:bg-gray-800',
      iconColor: 'text-gray-600 dark:text-gray-400',
      ring: 'ring-gray-200/80 dark:ring-gray-700/70',
      activeBg: 'bg-gray-50/80 dark:bg-gray-800/50',
    },
    {
      key: 'entra',
      count: entraCount,
      label: 'Entra ID Groups',
      icon: Globe,
      iconWrap: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      ring: 'ring-blue-200/80 dark:ring-blue-900/40',
      activeBg: 'bg-blue-50/60 dark:bg-blue-900/20',
    },
    {
      key: 'manual',
      count: manualCount,
      label: 'Manual Groups',
      icon: Shield,
      iconWrap: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
      ring: 'ring-purple-200/80 dark:ring-purple-900/40',
      activeBg: 'bg-purple-50/60 dark:bg-purple-900/20',
    },
  ]
  const isReader = isReadOnlyUser(authUser)

  // ─── Handlers ───

  const handleCreateOrUpdate = async (data) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setActionLoading(true)
    try {
      if (groupModal.group) {
        await groupsApi.update(groupModal.group.id, data)
        toast.success('Updated', `Group "${data.name}" updated`)
      } else {
        await groupsApi.create({ ...data, type: 'manual' })
        toast.success('Created', `Group "${data.name}" created`)
      }
      setGroupModal({ open: false, group: null })
      fetchData()
    } catch (err) {
      toast.error('Error', err.response?.data?.error || 'Failed to save group')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async () => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      setDeleteModal({ open: false, group: null })
      return
    }
    setActionLoading(true)
    try {
      await groupsApi.delete(deleteModal.group.id)
      toast.success('Deleted', `Group "${deleteModal.group.name}" deleted`)
      setDeleteModal({ open: false, group: null })
      fetchData()
    } catch (err) {
      toast.error('Error', 'Failed to delete group')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveMembers = async (userIds) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setActionLoading(true)
    try {
      await groupsApi.setMembers(membersModal.group.id, userIds)
      toast.success('Updated', 'Group members updated')
      setMembersModal({ open: false, group: null })
      fetchData()
    } catch (err) {
      toast.error('Error', err.response?.data?.error || 'Failed to update members')
    } finally {
      setActionLoading(false)
    }
  }

  const handleImportEntra = async (entraGroupId) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setActionLoading(true)
    try {
      const res = await groupsApi.importEntra(entraGroupId)
      const data = res.data
      const groupName = data?.group?.name || 'Entra group'
      const matched = data?.matched_user_count ?? 0
      const unmatched = data?.unmatched_member_count ?? 0
      toast.success(
        'Group Imported',
        `"${groupName}" imported successfully. ${matched} user${matched !== 1 ? 's' : ''} matched${unmatched > 0 ? `, ${unmatched} not yet in your organisation` : ''}.`
      )
      setImportModal(false)
      fetchData()
    } catch (err) {
      toast.error('Import Failed', err.response?.data?.error || 'Failed to import Entra group')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSync = async (group = membersModal.group) => {
    setActionLoading(true)
    try {
      const res = await groupsApi.sync(group.id)
      const matched = res.data?.matched_user_count ?? 0
      const unmatched = res.data?.unmatched_member_count ?? 0
      toast.success(
        'Sync Complete',
        `${matched} user${matched !== 1 ? 's' : ''} synced from Entra${unmatched > 0 ? `. ${unmatched} Entra member${unmatched !== 1 ? 's' : ''} not yet in your organisation.` : '.'}`
      )
      // Refresh the modal with updated data
      const refreshed = await groupsApi.get(group.id)
      if (membersModal.open) {
        setMembersModal({ open: true, group: refreshed.data })
      }
      fetchData()
    } catch (err) {
      toast.error('Sync Failed', err.response?.data?.error || 'Failed to sync Entra group')
    } finally {
      setActionLoading(false)
    }
  }

  const openMembers = async (group) => {
    try {
      const res = await groupsApi.get(group.id)
      setMembersModal({ open: true, group: res.data })
    } catch {
      toast.error('Error', 'Failed to load group details')
    }
  }

  if (loading) {
    return (
      <PageSkeleton />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <UsersIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Groups</h1>
              <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {groups.length}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage access by organizing users into groups. Import from Entra ID or create custom groups.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tabs.length > 0 && typeof onTabChange === 'function' && (
            <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 shadow-sm ring-1 ring-gray-100 dark:ring-gray-800/80">
              {tabs.map(tab => {
                const isActive = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => onTabChange(tab.key)}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-navy-800 text-white shadow-sm ring-1 ring-navy-900/20'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    aria-pressed={isActive}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </div>
          )}
          <button
            onClick={() => setImportModal(true)}
            disabled={isReader}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className="inline-flex items-center gap-2 h-11 px-5 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadCloud className="w-4 h-4" />
            Import from Entra
          </button>
          <button
            onClick={() => setGroupModal({ open: true, group: null })}
            disabled={isReader}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className="inline-flex items-center gap-2 h-11 px-5 bg-navy-800 text-white text-sm font-semibold rounded-xl shadow-sm ring-1 ring-navy-900/20 transition-all duration-200 hover:bg-navy-900 enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg enabled:active:translate-y-0 enabled:active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            New Group
          </button>
        </div>
      </div>

      {/* Stats */}
      {groups.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {statCards.map((card, idx) => {
            const isActive = groupTypeFilter === card.key
            const Icon = card.icon
            return (
              <button
                key={card.key}
                type="button"
                onClick={() => setGroupTypeFilter(card.key)}
                aria-pressed={isActive}
                className={`group text-left bg-white dark:bg-gray-900 rounded-xl border p-4 shadow-sm ring-1 transition-all duration-200 animate-fade-in ${
                  isActive
                    ? `${card.activeBg} border-transparent ${card.ring} shadow-md`
                    : 'border-gray-200 dark:border-gray-800 ring-transparent hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700'
                }`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105 ${card.iconWrap}`}>
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate">{card.count}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* Search + Type Filter + View Toggle - Single responsive row */}
      {groups.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups..."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Controls: Type Filter + View Toggle - right aligned */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-auto">
            {/* Type Filter */}
            <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-800/70">
              {[
                { value: 'all', label: 'All' },
                { value: 'entra', label: 'Entra ID' },
                { value: 'manual', label: 'Manual' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setGroupTypeFilter(value)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                    groupTypeFilter === value
                      ? 'bg-navy-800 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-navy-800 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-navy-800 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <UsersIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600" />
          <h3 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">No groups yet</h3>
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            Create a manual group to organize users, or import groups from Microsoft Entra ID.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              onClick={() => setImportModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <DownloadCloud className="w-4 h-4" /> Import from Entra
            </button>
            <button
              onClick={() => setGroupModal({ open: true, group: null })}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-navy-800 text-white rounded-lg hover:bg-navy-900"
            >
              <Plus className="w-4 h-4" /> Create Group
            </button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No groups match your search.</p>
      ) : viewMode === 'list' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Group</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Members</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Last Synced</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24"></th>
                </tr>
              </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map(group => {
                const isEntra = group.type === 'entra'
                return (
                  <tr
                    key={group.id}
                    onClick={() => openMembers(group)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isEntra ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
                        }`}>
                          {isEntra
                            ? <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            : <UsersIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{group.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{group.description || 'No description'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${
                        isEntra
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      }`}>
                        {isEntra ? 'Entra ID' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                        <UsersIcon className="w-3.5 h-3.5 text-gray-400" />
                        {group.member_count}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      {isEntra && group.last_synced_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3" />
                          {new Date(group.last_synced_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {isEntra && (
                          <button
                            onClick={() => handleSync(group)}
                            disabled={isReader}
                            className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isReader ? "Read-only access" : "Sync from Entra"}
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!isEntra && (
                          <button
                            onClick={() => !isReader && setGroupModal({ open: true, group })}
                            disabled={isReader}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isReader ? "Read-only access" : "Edit"}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModal({ open: true, group })}
                          disabled={isReader}
                          title={isReader ? READ_ONLY_MESSAGE : "Delete"}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              onEdit={() => setGroupModal({ open: true, group })}
              onDelete={() => setDeleteModal({ open: true, group })}
              onMembers={() => openMembers(group)}
              onSync={() => handleSync(group)}
              readOnly={isReader}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <GroupModal
        open={groupModal.open}
        group={groupModal.group}
        onClose={() => setGroupModal({ open: false, group: null })}
        onSave={handleCreateOrUpdate}
        loading={actionLoading}
      />
      <MembersModal
        open={membersModal.open}
        group={membersModal.group}
        orgUsers={orgUsers}
        onClose={() => setMembersModal({ open: false, group: null })}
        onSave={handleSaveMembers}
        onSync={() => handleSync(membersModal.group)}
        loading={actionLoading}
        readOnly={isReader}
      />
      <ConfirmModal
        open={deleteModal.open}
        title="Delete Group"
        message={`Are you sure you want to delete "${deleteModal.group?.name}"? This will remove all member associations and template access for this group. This cannot be undone.`}
        confirmLabel="Delete Group"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ open: false, group: null })}
        loading={actionLoading}
      />
      <ImportEntraModal
        open={importModal}
        onClose={() => setImportModal(false)}
        onImport={handleImportEntra}
        loading={actionLoading}
      />
    </div>
  )
}

// ─── Group Card ───
function GroupCard({ group, onEdit, onDelete, onMembers, onSync, readOnly }) {
  const isEntra = group.type === 'entra'

  const formatSyncTime = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now - d
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHrs = Math.floor(diffMins / 60)
    if (diffHrs < 24) return `${diffHrs}h ago`
    const diffDays = Math.floor(diffHrs / 24)
    return `${diffDays}d ago`
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
      {/* Color bar at top */}
      <div className={`h-1 ${isEntra ? 'bg-blue-500' : 'bg-purple-500'}`} />

      <div className="p-5">
        {/* Top row: icon + name + badge + actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isEntra ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
            }`}>
              {isEntra
                ? <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                : <UsersIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              }
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{group.name}</h3>
              <span className={`inline-block mt-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                isEntra
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
              }`}>
                {isEntra ? 'Entra ID' : 'Manual'}
              </span>
            </div>
          </div>
          <GroupActions
            group={group}
            onEdit={onEdit}
            onDelete={onDelete}
            onMembers={onMembers}
            onSync={onSync}
            readOnly={readOnly}
          />
        </div>

        {/* Description */}
        {group.description && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{group.description}</p>
        )}

        {/* Footer info */}
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <UsersIcon className="w-3.5 h-3.5" />
              {group.member_count} member{group.member_count !== 1 ? 's' : ''}
            </span>
            {isEntra && group.last_synced_at && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                <Clock className="w-3 h-3" />
                {formatSyncTime(group.last_synced_at)}
              </span>
            )}
          </div>
          <button
            onClick={onMembers}
            className={`text-xs font-medium transition-colors ${
              isEntra
                ? 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
                : 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300'
            }`}
          >
            {isEntra ? 'View Members' : 'Manage'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Group Actions Dropdown ───
function GroupActions({ group, onEdit, onDelete, onMembers, onSync, readOnly }) {
  const [open, setOpen] = useState(false)
  const isEntra = group.type === 'entra'

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg py-1">
            <button onClick={() => { onMembers(); setOpen(false) }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
              <UsersIcon className="w-4 h-4 text-gray-400" />
              {isEntra ? 'View Members' : 'Manage Members'}
            </button>
            {isEntra && (
              <button onClick={() => { if (!readOnly) { onSync(); setOpen(false) } }}
                disabled={readOnly}
                title={readOnly ? READ_ONLY_MESSAGE : undefined}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
                <RefreshCw className="w-4 h-4" /> Sync from Entra
              </button>
            )}
            {!isEntra && (
              <button onClick={() => { if (!readOnly) { onEdit(); setOpen(false) } }}
                disabled={readOnly}
                title={readOnly ? READ_ONLY_MESSAGE : undefined}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
                <Edit3 className="w-4 h-4 text-gray-400" /> Edit Group
              </button>
            )}
            <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
            <button onClick={() => { onDelete(); setOpen(false) }}
              disabled={readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
              <Trash2 className="w-4 h-4" /> Delete Group
            </button>
          </div>
        </>
      )}
    </div>
  )
}
